import { useCallback, useEffect, useRef, useState } from "react";

export type Async<T> = {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
};

type CacheEntry = { data: unknown; at: number };

const memory = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<unknown>>();
const SESSION_PREFIX = "fk_cache:";
let generation = 0;
const genListeners = new Set<() => void>();
const softListeners = new Set<() => void>();

function readSession<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(SESSION_PREFIX + key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeSession(key: string, data: unknown): void {
  try {
    sessionStorage.setItem(SESSION_PREFIX + key, JSON.stringify(data));
  } catch {
    /* private mode / quota */
  }
}

function cached<T>(key: string | undefined): T | null {
  if (!key) return null;
  const hit = memory.get(key);
  if (hit) return hit.data as T;
  if (key === "portfolio") {
    const seeded = readSession<T>("portfolio");
    if (seeded != null) {
      memory.set(key, { data: seeded, at: Date.now() });
      return seeded;
    }
  }
  return null;
}

/** Drop in-memory + session caches and refetch every live useAsync. */
export function bustAsyncCache(): void {
  memory.clear();
  inflight.clear();
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k?.startsWith(SESSION_PREFIX)) keys.push(k);
    }
    for (const k of keys) sessionStorage.removeItem(k);
  } catch {
    /* private mode */
  }
  generation += 1;
  for (const listener of genListeners) listener();
}

/**
 * Refetch every live read while leaving what is on screen alone.
 *
 * bustAsyncCache is the wrong tool for a background refresh: clearing the
 * cache sends every hook back through its empty state, so the screen drops to
 * a skeleton and builds itself again. Once during a sync that is a flicker;
 * on a timer, while a long backfill runs, it is a page that never finishes
 * loading — which is the exact impression this was added to remove.
 *
 * So this one swaps the data in underneath instead. No loading state, and a
 * failed refresh keeps the last good answer rather than replacing figures with
 * an error: the poll is speculative, and the screen's own reads still report
 * properly when the user asks for something.
 */
export function revalidateAsync(): void {
  memory.clear();
  inflight.clear();
  for (const listener of softListeners) listener();
}

/** Minimal fetch-on-mount with a manual reload. Pass `{ key }` to share
 *  a stale-while-revalidate cache across mounts and duplicate widgets. */
export function useAsync<T>(
  fn: () => Promise<T>,
  deps: unknown[],
  opts?: { key?: string },
): Async<T> {
  const key = opts?.key;
  const [data, setData] = useState<T | null>(() => cached<T>(key));
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(() => cached<T>(key) == null);
  const [nonce, setNonce] = useState(0);
  const [gen, setGen] = useState(generation);

  useEffect(() => {
    const listener = () => setGen(generation);
    genListeners.add(listener);
    return () => { genListeners.delete(listener); };
  }, []);

  // The caller owns the dependency list; fn is recreated on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const run = useCallback(fn, deps);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    const revalidate = () => {
      run().then(
        (d) => {
          if (key) {
            memory.set(key, { data: d, at: Date.now() });
            if (key === "portfolio") writeSession("portfolio", d);
          }
          if (!mounted.current) return;
          setData(d);
          setError(null);
          setLoading(false);
        },
        () => { /* background refresh: keep the last good answer */ },
      );
    };
    softListeners.add(revalidate);
    return () => { softListeners.delete(revalidate); };
  }, [run, key]);

  useEffect(() => {
    let live = true;
    const existing = cached<T>(key);
    if (existing != null) {
      setData(existing);
      setLoading(false);
    } else {
      setLoading(true);
    }

    const start = key && inflight.get(key) ? inflight.get(key)! : run();
    if (key && !inflight.has(key)) inflight.set(key, start);

    start.then(
      (d) => {
        if (key) {
          memory.set(key, { data: d, at: Date.now() });
          if (key === "portfolio") writeSession("portfolio", d);
          inflight.delete(key);
        }
        if (!live) return;
        setData(d as T);
        setError(null);
        setLoading(false);
      },
      (e: unknown) => {
        if (key) inflight.delete(key);
        if (!live) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      },
    );
    return () => { live = false; };
  }, [run, nonce, gen, key]);

  return { data, error, loading, reload: () => setNonce((n) => n + 1) };
}
